/**
 * Link abort signals: the linked signal aborts as soon as ANY input aborts, with that input's reason.
 *
 * Uses AbortSignal.any when the runtime has it, and otherwise a forwarding controller. Shared by the Supabase
 * archive fetch (lib/supabase/abortableFetch.ts) and the request deadline (lib/http/fetchWithDeadline.ts), so
 * there is one copy of the linking logic.
 */

export interface LinkedSignal {
  readonly signal: AbortSignal
  /** Stop forwarding (removes the fallback's listeners). Safe to call more than once. */
  dispose(): void
}

export function abortReason(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException('The operation was aborted', 'AbortError')
}

const noop = () => undefined

export function linkSignals(signals: readonly AbortSignal[]): LinkedSignal {
  // Read at call time: tests (and old browsers) may not have AbortSignal.any.
  const any = (AbortSignal as unknown as { any?: (inputs: AbortSignal[]) => AbortSignal }).any
  if (typeof any === 'function') return { signal: any.call(AbortSignal, [...signals]), dispose: noop }

  const controller = new AbortController()
  const already = signals.find((s) => s.aborted)
  if (already) {
    controller.abort(abortReason(already))
    return { signal: controller.signal, dispose: noop }
  }
  const forwards = signals.map((source) => {
    const forward = () => controller.abort(abortReason(source))
    source.addEventListener('abort', forward, { once: true })
    return () => source.removeEventListener('abort', forward)
  })
  // Close the check/listen race: an input may have aborted while the listeners were being attached.
  const late = signals.find((s) => s.aborted)
  if (late && !controller.signal.aborted) controller.abort(abortReason(late))
  return { signal: controller.signal, dispose: () => forwards.forEach((remove) => remove()) }
}
